import { useState, useEffect, useCallback } from 'react';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { pick } from '../../../shared/offer-domain/pick';

/**
 * GET /negotiation/{procurementId} — saved package for TE wizard prefill.
 * 404 or `success: false` means no package yet (`data` stays null).
 */
const useNegotiationPackage = (procurementId, { enabled = true } = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled && procurementId));

  const fetchPackage = useCallback(async () => {
    if (!procurementId || !enabled) return null;
    const token = getAuthToken();
    if (!token) {
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    try {
      // fetch, not axios: the global axios interceptors unwrap the envelope a
      // variable number of times and turn a 404 into a generic message.
      const response = await fetch(
        `${api.API_URL}/negotiation/${encodeURIComponent(procurementId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 404) {
        setData(null);
        return null;
      }
      const body = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          pick(body, 'message', 'Message') || response.statusText
        );
      const success = pick(body, 'success', 'Success');
      const pkg = success === false ? null : pick(body, 'data', 'Data');
      setData(pkg);
      return pkg;
    } catch (err) {
      notify.apiError(err, 'load saved negotiation selection');
      setData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [procurementId, enabled]);

  useEffect(() => {
    fetchPackage();
  }, [fetchPackage]);

  return {
    data,
    isLoading: enabled ? isLoading : false,
    refresh: fetchPackage,
  };
};

export default useNegotiationPackage;
